import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useLocation, useMount } from 'react-use'
import {
  useQueryParams,
  BooleanParam,
  NumberParam,
  StringParam,
  withDefault,
  createEnumParam,
} from 'use-query-params'
import ActivityLogPanel from 'src/components/molecules/ActivityLogPanel'
import StoreTenantsTabTemplate from 'src/components/templates/StoreTenantsTab'
import { DEFAULT_PAGE, STORE_TENANTS_PER_PAGE } from 'src/constants'
import { ToastTriggerContext } from 'src/context/toast.context'
import {
  ActivityLogEntityType,
  EVENT_TYPE_LABELS,
} from 'src/domain/activityLog'
import { isActivityLogEnabled } from 'src/domain/org'
import { useActivityLogPanel } from 'src/hooks/useActivityLogPanel'
import usePrevious from 'src/hooks/usePrevious'
import Path, { StorePathParams } from 'src/routes/path'
import { setStoreTenantsParams } from 'src/slices/query/querySlice'
import { ActivityLogScopeTypeEnum, RoleEnum } from 'src/slices/services/api'
import { TenantStatusEnum } from 'src/slices/services/api/api'
import { selectStoreByCode } from 'src/slices/stores/storesSlice'
import {
  getStoreTenants,
  selectTenantsByParams,
  selectTenantStateByParams,
} from 'src/slices/tenants/tenantsSlice'
import { useAppSelector, useAppDispatch } from 'src/store'
import Presenter, { optionDefaultValue } from './presenter'

type StoreTenantsTabProps = { showsUnderline?: boolean; stickyTop?: number }

type FilterValues = { status: TenantStatusEnum | string }

const StoreTenantsTab: React.FC<StoreTenantsTabProps> = ({
  showsUnderline,
  stickyTop,
}: StoreTenantsTabProps): ReactElement => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const toastContext = useContext(ToastTriggerContext)

  // Query
  const storedParams = useAppSelector((state) => state.query.storeTenantsParams)
  const { orgCode, storeCode } = useParams<StorePathParams>() as StorePathParams
  const { pathname } = useLocation()
  const queryConfig = {
    tab: StringParam,
    page: withDefault(NumberParam, DEFAULT_PAGE),
    q: withDefault(StringParam, undefined),
    status: withDefault(
      createEnumParam([
        TenantStatusEnum.Ready,
        TenantStatusEnum.NotReady,
        TenantStatusEnum.Closed,
      ]),
      undefined
    ),
    trained: withDefault(BooleanParam, undefined),
  }
  const [{ tab, page, q, status, trained }, setQuery] =
    useQueryParams(queryConfig)

  useMount(() => {
    if (pathname === storedParams.pathname) {
      setQuery({
        tab,
        page: storedParams.page,
        q: storedParams.q,
        status: storedParams.status,
        trained: storedParams.trained,
      })
    }
  })

  // API Request
  const params = useMemo(() => {
    return {
      orgCode,
      storeCode,
      page,
      perPage: STORE_TENANTS_PER_PAGE,
      q,
      status,
      trained,
    }
  }, [orgCode, storeCode, page, q, status, trained])
  const paginatedTenants = useAppSelector((state) =>
    selectTenantsByParams(params)(state)
  )
  const store = useAppSelector((state) =>
    selectStoreByCode({ orgCode, storeCode })(state)
  )

  const {
    isOpen: isLogPanelOpen,
    open: openLogPanel,
    close: closeLogPanel,
    logs: activityLogs,
    isLoading: isActivityLogsLoading,
    isLoadingMore: isActivityLogsLoadingMore,
    hasMore: hasMoreActivityLogs,
    dateRangeFilter: activityLogDateRangeFilter,
    errorMessage: activityLogError,
    onLoadMore: handleLoadMoreActivityLogs,
  } = useActivityLogPanel({
    orgCode,
    entityType: ActivityLogEntityType.Tenant,
    scopeType: ActivityLogScopeTypeEnum.Store,
    scopeId: store?.id,
  })

  const templateTenants = useMemo(() => {
    return (
      paginatedTenants?.map((tenant) => {
        return Presenter.mapTemplateStore(orgCode, storeCode, tenant)
      }) ?? []
    )
  }, [orgCode, storeCode, paginatedTenants])

  const requestState = useAppSelector((state) =>
    selectTenantStateByParams(params)(state)
  )
  const role: RoleEnum | undefined = useAppSelector(
    (state) => state.auth.currentAuthorization?.role
  )

  useEffect(() => {
    dispatch(getStoreTenants(params))
  }, [dispatch, params])
  useEffect(() => {
    dispatch(
      setStoreTenantsParams({
        tab,
        page: params.page,
        q: params.q,
        status: params.status,
        pathname,
      })
    )
  }, [dispatch, params.page, params.q, params.status, pathname, tab])

  // Form
  const formMethods = useForm<FilterValues>({
    // Query Paramsの値をデフォルト値にする
    defaultValues: { status },
  })
  const watchFilters = formMethods.watch()
  const prevAmount = usePrevious(watchFilters)
  const filters = Presenter.filters()

  // フィルターの変更をQuery Paramsに反映する
  useEffect(() => {
    const filterValue = Presenter.statusQueryParams(watchFilters.status)
    if (filterValue !== status) {
      if (prevAmount?.status === watchFilters.status) {
        formMethods.setValue('status', status ?? optionDefaultValue)
      } else {
        setQuery({ page: DEFAULT_PAGE, status: filterValue })
      }
    }
  }, [watchFilters.status, status, prevAmount?.status, setQuery, formMethods])

  // FIXME: filter と query の関係箇所を一元化したい
  useMount(() => {
    if (pathname === storedParams.pathname) {
      formMethods.setValue('status', storedParams.status ?? optionDefaultValue)
    }
  })

  // trained filter
  const [isChecked, setIsChecked] = useState(trained)
  useMount(() => {
    if (pathname === storedParams.pathname) {
      setIsChecked(storedParams.trained ?? false)
    }
  })

  // Handler
  const handlePaginationClick = (move: number) => {
    setQuery({ page: move })
  }

  const handleSearchFieldChange = (text: string) => {
    setQuery({ page: DEFAULT_PAGE, q: text })
  }

  const handleChangeCheck = () => {
    setIsChecked(!isChecked)
    // true(=研修実施あり)は使わないため undefined にする
    const trainedValue = isChecked ? undefined : false
    setQuery({ page: DEFAULT_PAGE, trained: trainedValue })
  }

  const handleOpenLogPanel = useCallback(() => {
    if (!store?.id) {
      toastContext.sendToast({
        variant: 'error',
        title: '店舗情報が取得できませんでした',
      })
      return
    }
    openLogPanel()
  }, [store?.id, toastContext, openLogPanel])

  return (
    <FormProvider {...formMethods}>
      <form>
        <StoreTenantsTabTemplate
          isLoading={requestState.status === 'loading'}
          currentPage={page}
          filters={filters}
          tenantCount={requestState.totalCount ?? 0} // TODO undefinedにする
          tenants={templateTenants}
          rowsPerPage={STORE_TENANTS_PER_PAGE}
          defaultSearchValue={storedParams.q}
          isChecked={isChecked}
          showsUnderline={showsUnderline}
          stickyTop={stickyTop}
          role={role}
          orgCode={orgCode}
          paginationOnClick={handlePaginationClick}
          searchFieldOnChange={handleSearchFieldChange}
          onChangeCheck={handleChangeCheck}
          onClickAddTenant={() =>
            navigate(Path.createTenants(orgCode, storeCode))
          }
          onClickEditTenant={() =>
            navigate(Path.updateTenants(orgCode, storeCode))
          }
          onClickHistoryButton={
            isActivityLogEnabled(orgCode) ? handleOpenLogPanel : undefined
          }
        />
      </form>
      <ActivityLogPanel
        orgCode={orgCode}
        open={isLogPanelOpen}
        title="変更履歴"
        logs={activityLogs}
        isLoading={isActivityLogsLoading}
        isLoadingMore={isActivityLogsLoadingMore}
        hasMore={hasMoreActivityLogs}
        eventTypeLabels={EVENT_TYPE_LABELS}
        dateRangeFilter={activityLogDateRangeFilter}
        errorMessage={activityLogError}
        onClose={closeLogPanel}
        onLoadMore={handleLoadMoreActivityLogs}
      />
    </FormProvider>
  )
}

StoreTenantsTab.defaultProps = { showsUnderline: false, stickyTop: 0 }

export default StoreTenantsTab
